// INCREMENTAL CROSSING COUNT for the refine's trial loop (TGCLOUD-1909, out of
// TGCLOUD-1908's QA round 7). crossingRefine's `measure()` runs on EVERY trial move,
// and each call rebuilt the scoring model and re-ran the whole crossing detector:
// about 2 ms a trial, a couple of thousand trials a run, several runs a layout — 72
// to 76 per cent of a 278-person layout (G-1-01441: 5 s unlocked, 8–9 s locked).
//
// A trial moves a few people, so almost every edge keeps its geometry from one call
// to the next. The counter keeps persistent state between calls: each edge's
// geometry (the numbers the pair test reads), its segments and node info, the two
// broad-phase grids, and per edge which partner edges put hooks on it. A call walks
// the edges once, re-decomposes only the ones whose geometry CHANGED since the last
// call, re-runs exactly the grid pairs that involve one of them, and keeps every
// other pair's outcome. The count is the number of edges carrying at least one hook
// — exactly `detectAllSegmentIntersections(edges, nodesById).size`: the hook lands on
// the steeper segment's edge, and processSegmentPair decides each pair from the two
// segments and the two edges' node infos alone, so an unchanged pair of unchanged
// edges has an unchanged outcome. The baseline is simply the previous call, accepted
// or not: a rejected trial is restored before the next one, so the dirty set is at
// most two trials' movers.
//
// `VERIFY_INCREMENTAL_CROSSING_COUNT` runs the full detector beside it and throws on
// a mismatch; the corpus gate turns it on, so every call of every refine is checked.

import { config } from './config.js';
import {
  decomposePathIntoSegments,
  detectAllSegmentIntersections,
  edgeNodeInfo,
  processSegmentPair
} from './lineIntersection.js';

// The key processSegmentPair records a hook under (its `edgeKey`).
const hookKeyOf = (seg) =>
  seg.parentEdge.kind === 'twin-group'
    ? seg.edgeId
    : seg.parentEdge.from + '-' + seg.parentEdge.to;

const NONE = -1e300; // a missing node or field, distinct from any coordinate

// The node ids the pair test reads for `edge`: its endpoints, its twins and the
// union's partners (edgeNodeInfo's pid set). Parsed once per edge, kept on its entry.
function dependenciesOf(edge) {
  const deps = [edge.from];
  if (edge.kind === 'twin-group') {
    for (const pid of edge.twinIds || []) deps.push(pid);
  } else {
    deps.push(edge.to);
  }
  if (typeof edge.from === 'string' && edge.from.startsWith('union-')) {
    for (const p of edge.from.slice('union-'.length).split('-')) {
      deps.push(/^\d+$/.test(p) ? Number(p) : p);
    }
  }
  return deps;
}

// The numbers the pair test reads: each dependency's position and score lane, and
// the lane fields decompose reads. Written into `out` (reused across calls).
function geometryOf(edge, deps, nodesById, out) {
  out.length = 0;
  for (const id of deps) {
    const n = id != null ? nodesById.get(id) : null;
    if (n) out.push(n.x, n.y, n.scoreLaneY ?? NONE);
    else out.push(NONE, NONE, NONE);
  }
  out.push(
    edge.laneY ?? NONE,
    edge.scoreLaneY ?? NONE,
    edge.laneDrawnOnly ? 1 : 0
  );
  return out;
}

const sameNumbers = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

export function createCrossingCounter() {
  // uid → entry { uid, key: number[], segments, info, cells: [[grid, cellKey, item]], stamp,
  //               vertical: items, spanner: items }
  const entries = new Map();
  // kind → from → to → uid (so a call builds no strings for an edge it has seen)
  const uidIndex = new Map();
  const spanGrid = new Map(); // cellKey → [{ seg, entry }]
  const vertGrid = new Map();
  const hooksOn = new Map(); // uid → Map(partnerUid → hooks on uid from partner)
  const hooksBy = new Map(); // uid → Set(target uids this edge put hooks on)
  const totals = new Map(); // uid → hooks on it
  let positive = 0; // edges with at least one hook — the count
  let stamp = 0;
  const scratch = [];
  const tmp = new Map();
  let pairStamp = 0; // marks the items already paired with the current dirty segment
  const nodePos = new Map(); // node id → [x, y, scoreLaneY] at the last call
  const moved = new Set(); // node ids whose position changed since the last call
  const infoByEdgeId = new Map(); // persistent: refreshed with each entry

  const uidOf = (edge) => {
    const kind = edge.kind;
    const to =
      kind === 'twin-group'
        ? [...(edge.twinIds || [])].sort((a, b) => a - b).join('-')
        : edge.to;
    let byFrom = uidIndex.get(kind);
    if (!byFrom) uidIndex.set(kind, (byFrom = new Map()));
    let byTo = byFrom.get(edge.from);
    if (!byTo) byFrom.set(edge.from, (byTo = new Map()));
    let uid = byTo.get(to);
    if (!uid) {
      uid =
        kind === 'twin-group'
          ? `${kind}:${edge.from}-twins-${to}`
          : `${kind}:${edge.from}-${to}`;
      byTo.set(to, uid);
    }
    return uid;
  };

  const CELL = () => (config.NODE_GAP > 0 ? config.NODE_GAP : 1);
  const insert = (grid, cellKey, item, entry) => {
    let arr = grid.get(cellKey);
    if (!arr) grid.set(cellKey, (arr = []));
    arr.push(item);
    entry.cells.push([grid, cellKey, item]);
  };
  const unplace = (entry) => {
    for (const [grid, cellKey, item] of entry.cells) {
      const arr = grid.get(cellKey);
      if (!arr) continue;
      const i = arr.indexOf(item);
      if (i >= 0) arr.splice(i, 1);
      if (!arr.length) grid.delete(cellKey);
    }
    entry.cells.length = 0;
    entry.verticals.length = 0;
    entry.spanners.length = 0;
  };
  const place = (entry) => {
    const cell = CELL();
    const colOf = (x) => Math.floor(x / cell);
    const rowOf = (y) => Math.floor(y / cell);
    for (const seg of entry.segments) {
      const item = { seg, entry, pairStamp: 0 };
      if (seg.type === 'vertical') {
        entry.verticals.push(item);
        const col = colOf(seg.x1);
        const r0 = rowOf(Math.min(seg.y1, seg.y2));
        const r1 = rowOf(Math.max(seg.y1, seg.y2));
        for (let r = r0; r <= r1; r++)
          insert(vertGrid, col + ':' + r, item, entry);
      } else {
        entry.spanners.push(item);
        const c0 = colOf(Math.min(seg.x1, seg.x2));
        const c1 = colOf(Math.max(seg.x1, seg.x2));
        const r0 = rowOf(Math.min(seg.y1, seg.y2));
        const r1 = rowOf(Math.max(seg.y1, seg.y2));
        for (let c = c0; c <= c1; c++)
          for (let r = r0; r <= r1; r++)
            insert(spanGrid, c + ':' + r, item, entry);
      }
    }
  };

  // Forget every hook an edge put on others and every hook others put on it.
  const addHooks = (uid, partner, n) => {
    let m = hooksOn.get(uid);
    if (!m) hooksOn.set(uid, (m = new Map()));
    m.set(partner, (m.get(partner) || 0) + n);
    let by = hooksBy.get(partner);
    if (!by) hooksBy.set(partner, (by = new Set()));
    by.add(uid);
    const was = totals.get(uid) || 0;
    const now = was + n;
    totals.set(uid, now);
    if (was <= 0 && now > 0) positive++;
    else if (was > 0 && now <= 0) positive--;
  };
  const forgetHooks = (uid) => {
    const by = hooksBy.get(uid);
    if (by) {
      for (const target of by) {
        const m = hooksOn.get(target);
        if (!m) continue;
        const n = m.get(uid) || 0;
        m.delete(uid);
        if (n) {
          const was = totals.get(target) || 0;
          const now = was - n;
          totals.set(target, now);
          if (was > 0 && now <= 0) positive--;
        }
      }
      hooksBy.delete(uid);
    }
    const on = hooksOn.get(uid);
    if (on) {
      for (const partner of on.keys()) {
        const pb = hooksBy.get(partner);
        if (pb) pb.delete(uid);
      }
      hooksOn.delete(uid);
    }
    if ((totals.get(uid) || 0) > 0) positive--;
    totals.delete(uid);
  };

  const full = (edges, nodesById) => {
    const m = detectAllSegmentIntersections(edges, nodesById);
    return m ? m.size ?? 0 : 0;
  };

  const count = (edges, nodesById) => {
    if (config.ENABLE_INCREMENTAL_CROSSING_COUNT === false) {
      return full(edges, nodesById);
    }
    stamp++;
    // Which nodes moved since the last call: the only way an edge's geometry can
    // change is through a node it depends on, or a lane field of its own.
    moved.clear();
    for (const [id, n] of nodesById) {
      const was = nodePos.get(id);
      const lane = n.scoreLaneY ?? NONE;
      if (!was) {
        nodePos.set(id, [n.x, n.y, lane]);
        moved.add(id);
      } else if (was[0] !== n.x || was[1] !== n.y || was[2] !== lane) {
        was[0] = n.x;
        was[1] = n.y;
        was[2] = lane;
        moved.add(id);
      }
    }
    const dirty = [];
    for (const edge of edges) {
      const uid = uidOf(edge);
      let entry = entries.get(uid);
      if (
        entry &&
        (edge.laneY ?? NONE) === entry.laneY &&
        (edge.scoreLaneY ?? NONE) === entry.scoreLaneY &&
        !!edge.laneDrawnOnly === entry.laneDrawnOnly &&
        (!moved.size || !entry.deps.some((id) => moved.has(id)))
      ) {
        entry.stamp = stamp;
        continue;
      }
      const deps = entry ? entry.deps : dependenciesOf(edge);
      geometryOf(edge, deps, nodesById, scratch);
      if (entry && sameNumbers(entry.key, scratch)) {
        // A dependency moved but this edge reads the same numbers (a node it
        // shares with a moved union that did not move it) — clean.
        entry.stamp = stamp;
        continue;
      }
      if (!entry) {
        entry = {
          uid,
          deps,
          key: [],
          laneY: NONE,
          scoreLaneY: NONE,
          laneDrawnOnly: false,
          segments: [],
          info: null,
          cells: [],
          verticals: [],
          spanners: [],
          stamp
        };
        entries.set(uid, entry);
      } else {
        unplace(entry);
        forgetHooks(uid);
        if (entry.info) infoByEdgeId.delete(entry.segments[0].edgeId);
      }
      entry.stamp = stamp;
      entry.key = scratch.slice();
      entry.laneY = edge.laneY ?? NONE;
      entry.scoreLaneY = edge.scoreLaneY ?? NONE;
      entry.laneDrawnOnly = !!edge.laneDrawnOnly;
      entry.segments = decomposePathIntoSegments(edge, nodesById);
      entry.segments.forEach((seg, i) => {
        seg.parentEdge = edge;
        seg._idx = i;
      });
      entry.info = entry.segments.length ? edgeNodeInfo(edge, nodesById) : null;
      if (entry.info) infoByEdgeId.set(entry.segments[0].edgeId, entry.info);
      place(entry);
      entry.dirtyStamp = stamp;
      dirty.push(entry);
    }
    // Edges gone since the last call (never within a refine, but kept honest).
    if (entries.size !== edges.length) {
      for (const [uid, entry] of entries) {
        if (entry.stamp === stamp) continue;
        unplace(entry);
        forgetHooks(uid);
        if (entry.info) infoByEdgeId.delete(entry.segments[0].edgeId);
        entries.delete(uid);
      }
    }
    for (const id of nodePos.keys()) if (!nodesById.has(id)) nodePos.delete(id);
    if (dirty.length) {
      tmp.clear();
      const run = (vItem, hItem) => {
        if (vItem.entry === hItem.entry) return;
        const v = vItem.seg;
        const h = hItem.seg;
        const vKey = hookKeyOf(v);
        const hKey = hookKeyOf(h);
        const nv = (tmp.get(vKey) || []).length;
        const nh = (tmp.get(hKey) || []).length;
        processSegmentPair(v, h, infoByEdgeId, tmp, false);
        const dv = (tmp.get(vKey) || []).length - nv;
        const dh = (tmp.get(hKey) || []).length - nh;
        if (dv) addHooks(vItem.entry.uid, hItem.entry.uid, dv);
        if (dh) addHooks(hItem.entry.uid, vItem.entry.uid, dh);
      };
      const cell = CELL();
      const colOf = (x) => Math.floor(x / cell);
      const rowOf = (y) => Math.floor(y / cell);
      for (const entry of dirty) {
        // Its verticals against every spanner sharing a cell.
        for (const vItem of entry.verticals) {
          const seg = vItem.seg;
          const col = colOf(seg.x1);
          const r0 = rowOf(Math.min(seg.y1, seg.y2));
          const r1 = rowOf(Math.max(seg.y1, seg.y2));
          pairStamp++;
          for (let r = r0; r <= r1; r++) {
            const arr = spanGrid.get(col + ':' + r);
            if (!arr) continue;
            for (const hItem of arr) {
              if (hItem.pairStamp === pairStamp) continue;
              hItem.pairStamp = pairStamp;
              run(vItem, hItem);
            }
          }
        }
        // Its spanners against every CLEAN vertical sharing a cell (a dirty
        // vertical met this spanner from its own side above).
        for (const hItem of entry.spanners) {
          const seg = hItem.seg;
          const c0 = colOf(Math.min(seg.x1, seg.x2));
          const c1 = colOf(Math.max(seg.x1, seg.x2));
          const r0 = rowOf(Math.min(seg.y1, seg.y2));
          const r1 = rowOf(Math.max(seg.y1, seg.y2));
          pairStamp++;
          for (let c = c0; c <= c1; c++) {
            for (let r = r0; r <= r1; r++) {
              const arr = vertGrid.get(c + ':' + r);
              if (!arr) continue;
              for (const vItem of arr) {
                if (vItem.entry.dirtyStamp === stamp) continue; // met from its own side
                if (vItem.pairStamp === pairStamp) continue;
                vItem.pairStamp = pairStamp;
                run(vItem, hItem);
              }
            }
          }
        }
      }
    }
    const result = positive;
    if (config.VERIFY_INCREMENTAL_CROSSING_COUNT) {
      const expected = full(edges, nodesById);
      if (expected !== result) {
        throw new Error(
          `incremental crossing count ${result} differs from the detector's ${expected}`
        );
      }
    }
    return result;
  };

  return { count };
}
